import React, { useEffect, useState } from 'react';
import { FaHandsHelping, FaUserShield, FaDollarSign, FaRegLightbulb } from 'react-icons/fa';
import '../styles/global.css'; // Ensure this import is present for styles

function WhyChooseUs() {
  useEffect(() => {
    const sections = document.querySelectorAll('.choose-section');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-active');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    sections.forEach(section => observer.observe(section));
  }, []);

  const [openIndex, setOpenIndex] = useState(null); // State for FAQ accordion

  const toggleAccordion = index => {
    setOpenIndex(openIndex === index ? null : index); // Toggle open/close
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <h2 className="text-4xl font-semibold mb-12 text-gray-900 text-center">Why Choose CrossTax Advisors?</h2>

      <section className="choose-section mb-8 fade-in">
        <div className="flex items-start mb-6">
          <FaHandsHelping className="text-accent text-3xl mr-4" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Flexibility</h3>
            <p className="text-lg text-gray-700">
              We offer customized service models tailored to your needs, whether you need dedicated CPAs or a combination of outsourced and in-house solutions.
            </p>
            <p className="text-lg text-gray-600">Choose a plan that fits your business size and needs.</p>
          </div>
        </div>
      </section>

      <section className="choose-section mb-8 fade-in">
        <div className="flex items-start mb-6">
          <FaUserShield className="text-accent text-3xl mr-4" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Specialization</h3>
            <p className="text-lg text-gray-700">
              Our team specializes in complex tax areas, including high-net-worth individual taxation, international tax structuring, and real estate taxation, ensuring deep expertise and strategic insights.
            </p>
            <p className="text-lg text-gray-600">We stay updated on the latest tax regulations to serve you better.</p>
          </div>
        </div>
      </section>

      <section className="choose-section mb-8 fade-in">
        <div className="flex items-start mb-6">
          <FaDollarSign className="text-accent text-3xl mr-4" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">US-Based Tax Agents</h3>
            <p className="text-lg text-gray-700">
              Our US-based tax agents provide direct support, ensuring that your data remains in-country and that you receive prompt, responsive service tailored to your specific needs.
            </p>
            <p className="text-lg text-gray-600">Experience the peace of mind that comes with local expertise.</p>
          </div>
        </div>
      </section>

      <section className="choose-section mb-8 fade-in">
        <div className="flex items-start mb-6">
          <FaRegLightbulb className="text-accent text-3xl mr-4" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Innovative Solutions</h3>
            <p className="text-lg text-gray-700">
              We leverage advanced technology and data analytics to provide insights that drive better tax strategies and compliance.
            </p>
            <p className="text-lg text-gray-600">Our proactive approach ensures you stay ahead of the curve.</p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="choose-section mb-8 fade-in">
        <h2 className="text-3xl font-semibold mb-6 text-gray-900 text-center">What Our Clients Say</h2>
        <div className="space-y-4">
          {[
            { text: "CrossTax Advisors helped us navigate the complexities of international taxation with ease. Their expertise was invaluable!", name: "John Doe", title: "CEO of Global Investments", rating: 5 },
            { text: "The team at CrossTax is knowledgeable and responsive. They truly care about their clients' success!", name: "Jane Smith", title: "CFO of Tech Solutions Inc.", rating: 4 },
            { text: "Thanks to CrossTax Advisors, our tax filing process has never been smoother. Their team is professional and efficient!", name: "Emily Chen", title: "Owner of Creative Media Co.", rating: 5 },
            { text: "I was impressed with the level of service and attention to detail provided by CrossTax Advisors. Highly recommend!", name: "David Kim", title: "Managing Partner at FinTech Innovations", rating: 5 },
            { text: "CrossTax Advisors has been instrumental in helping us understand our tax obligations across borders. Their guidance is priceless!", name: "Sarah Johnson", title: "VP of Operations at Global Logistics", rating: 5 },
          ].map((testimonial, index) => (
            <blockquote key={index} className="p-4 border rounded shadow-sm bg-gray-50 hover:bg-gray-100 transition duration-300">
              <p className="text-gray-700 italic">"{testimonial.text}"</p>
              <footer className="text-gray-600 mt-2">– {testimonial.name}, {testimonial.title}</footer>
              <div className="flex mt-2">
                <span className="text-accent">{"⭐".repeat(testimonial.rating)}{"☆".repeat(5 - testimonial.rating)}</span>
              </div>
            </blockquote>
          ))}
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="choose-section mb-8 fade-in">
        <h2 className="text-3xl font-semibold mb-6 text-gray-900 text-center">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { question: "What makes CrossTax Advisors different?", answer: "We offer a unique combination of flexibility, specialization, and US-based support to meet your tax advisory needs." },
            { question: "How do I get started?", answer: "Simply contact us through our website or give us a call, and we’ll guide you through the process." },
            { question: "What services do you provide?", answer: "We offer a range of services including cross-border tax advisory, compliance, and strategic tax planning." },
          ].map((item, index) => (
            <div key={index}>
              <button onClick={() => toggleAccordion(index)} className="w-full text-left p-4 bg-gray-100 border rounded hover:bg-gray-200 transition duration-300">
                {item.question}
              </button>
              {openIndex === index && (
                <div className="p-4 bg-white border-l-4 border-accent">
                  <p className="text-gray-600">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <div className="text-center mt-12">
        <button className="btn-primary px-8 py-3 bg-accent text-white font-semibold rounded-full hover:bg-blue-600 hover:scale-105 hover:shadow-lg transition transform duration-300">
          Contact Us to Learn More
        </button>
      </div>
    </div>
  );
}

export default WhyChooseUs;
